// import React, { createContext, useState, useContext, useEffect } from 'react';
// import authService from '../services/authService';
// import { setCurrentUser, clearCurrentUser, logPermissions } from '../utils/permissions';

// const AuthContext = createContext();

// export function useAuth() {
//   return useContext(AuthContext);
// }

// export function AuthProvider({ children }) {
//   const [user, setUser] = useState(null);
//   const [loading, setLoading] = useState(true);

//   useEffect(() => {
//     checkAuth();
//   }, []);

//   async function checkAuth() {
//     console.log('Checking auth...'); // DEBUG
//     if (authService.isAuthenticated()) {
//       const currentUser = authService.getCurrentUser();
//       console.log('User found:', currentUser); // DEBUG
//       setUser(currentUser);
      
//       // ⭐ SET USER IN PERMISSIONS SYSTEM
//       setCurrentUser(currentUser);
      
//       // Debug permissions in development
//       if (process.env.NODE_ENV === 'development') {
//         console.log('🔐 Permissions loaded for user:', currentUser.username);
//         logPermissions();
//       }
//     } else {
//       console.log('No user found'); // DEBUG
//       clearCurrentUser();
//     }
//     setLoading(false);
//   }

//   async function login(username, password) {
//     const result = await authService.login(username, password);
//     if (result.success) {
//       setUser(result.user);
      
//       // ⭐ SET USER IN PERMISSIONS SYSTEM
//       setCurrentUser(result.user);
      
//       // Debug permissions in development
//       if (process.env.NODE_ENV === 'development') {
//         console.log('🔐 User logged in with permissions:', result.user.username);
//         logPermissions();
//       }
//     }
//     return result;
//   }

//   async function logout() {
//     await authService.logout();
//     setUser(null);
    
//     // ⭐ CLEAR USER FROM PERMISSIONS SYSTEM
//     clearCurrentUser();
//   }

//   const value = {
//     user,
//     login,
//     logout,
//     isAuthenticated: !!user
//   };

//   if (loading) {
//     return (
//       <div style={{
//         display: 'flex',
//         alignItems: 'center',
//         justifyContent: 'center',
//         minHeight: '100vh',
//         background: '#f5f5f5',
//         fontSize: '18px',
//         color: '#666'
//       }}>
//         Loading...
//       </div>
//     );
//   }

//   return (
//     <AuthContext.Provider value={value}>
//       {children}
//     </AuthContext.Provider>
//   );
// }
// src/context/AuthContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';
import authService from '../services/authService';
import deviceService from '../services/deviceService';
import { setCurrentUser, clearCurrentUser, logPermissions } from '../utils/permissions';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  // Periodic device check-in
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(async () => {
      if (navigator.onLine && deviceService.needsCheckin()) {
        // console.log('🔄 Device needs check-in, validating...');
        await validateDeviceRegistration();
      }
    }, 60 * 60 * 1000);

    return () => clearInterval(interval);
  }, [user]);

  async function validateDeviceRegistration() {
    if (!navigator.onLine) return;

    try {
      const validation = await deviceService.validateDevice();
      
      if (!validation.valid) {
        if (validation.error === 'not_registered') {
          // console.log('📱 Registering device...');
          const result = await deviceService.registerDevice();
          
          if (result.success) {
            // console.log('✅ Device registered successfully');
          } else if (result.error === 'device_limit_reached') {
            // console.warn('⚠️ Device limit reached');
            alert(
              `⚠️ Device Limit Notice\n\n` +
              `Your plan allows ${result.devices_limit} devices.\n` +
              `Currently using: ${result.devices_used}\n\n` +
              `You can continue using this device, but please deactivate unused devices in Settings or upgrade your plan.`
            );
          }
        } else if (validation.error === 'checkin_required') {
          console.warn('⚠️ Device needs online check-in soon');
        }
      } else {
        // console.log('✅ Device validated successfully');
      }
    } catch (error) {
      console.error('Device validation failed:', error);
    }
  }

  async function checkAuth() {
    // console.log('Checking auth...');
    if (authService.isAuthenticated()) {
      const currentUser = authService.getCurrentUser();
      // console.log('User found:', currentUser);
      setUser(currentUser);
      setCurrentUser(currentUser);
      
      if (navigator.onLine) {
        await validateDeviceRegistration();
      }
      
      if (process.env.NODE_ENV === 'development') {
        // console.log('🔐 Permissions loaded for user:', currentUser.username);
        logPermissions();
      }
    } else {
      // console.log('No user found');
      clearCurrentUser();
    }
    setLoading(false);
  }

  async function login(username, password) {
    const result = await authService.login(username, password);
    if (result.success) {
      setUser(result.user);
      setCurrentUser(result.user);
      
      if (navigator.onLine) {
        try {
          const deviceResult = await deviceService.registerDevice();
          
          if (deviceResult.success) {
            // console.log('✅ Device registered:', deviceResult);
          } else if (deviceResult.error === 'device_limit_reached') {
            // console.warn('⚠️ Device limit reached');
            setTimeout(() => {
              alert(
                `⚠️ Device Limit Reached\n\n` +
                `Your plan allows ${deviceResult.devices_limit} devices.\n` +
                `Currently using: ${deviceResult.devices_used}\n\n` +
                `You can continue using this device, but please:\n` +
                `• Deactivate unused devices in Settings, or\n` +
                `• Upgrade your plan for more devices`
              );
            }, 1000);
          }
        } catch (deviceError) {
          // console.error('Device registration failed:', deviceError);
        }
      }
      
      if (process.env.NODE_ENV === 'development') {
        // console.log('🔐 User logged in with permissions:', result.user.username);
        logPermissions();
      }
    }
    return result;
  }

  async function logout() {
    await authService.logout();
    setUser(null);
    clearCurrentUser();
    localStorage.removeItem('device_token');
    localStorage.removeItem('last_online_checkin');
  }

  const value = {
    user,
    login,
    logout,
    isAuthenticated: !!user
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: '#f5f5f5',
        fontSize: '18px',
        color: '#666'
      }}>
        Loading...
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
